// components/VerifyCodeInput.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled, { useTheme } from '~/ui/styled'
import { Button, ColumnFlex, Text } from "~components/El"
import RNOTPInput from './components/RNOTPInput'

interface VerifyCodeInputProps {
  loading?: boolean
  length?: number
  onSubmit: (code: string) => void
  onResend?: () => void | Promise<void>
  resendText?: string
  resendCooldown?: number
}

export const VerifyCodeInput: React.FC<VerifyCodeInputProps> = ({
  loading = false,
  length = 6,
  onSubmit,
  onResend,
  resendText = '没收到验证码?',
  resendCooldown = 60,
}) => {
  const theme = useTheme()
  const [code, setCode] = useState('')
  const [timeLeft, setTimeLeft] = useState(0)
  const [isInvalid, setIsInvalid] = useState(false)
  const [resending, setResending] = useState(false)
  const onResendRef = useRef(onResend)
  const timeLeftRef = useRef(timeLeft)
  const resendingRef = useRef(resending)
  onResendRef.current = onResend
  timeLeftRef.current = timeLeft
  resendingRef.current = resending

  useEffect(() => {
    if (timeLeft <= 0) return undefined
    const timer = setTimeout(() => setTimeLeft((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [timeLeft])

  const runResend = useCallback(async () => {
    if (!onResendRef.current || timeLeftRef.current > 0 || resendingRef.current) return
    setResending(true)
    resendingRef.current = true
    try {
      await onResendRef.current()
      setTimeLeft(resendCooldown)
      timeLeftRef.current = resendCooldown
    } catch {
      // 发送失败时不启动倒计时，由 onResend 内提示错误
    } finally {
      setResending(false)
      resendingRef.current = false
    }
  }, [resendCooldown])

  const initialSendDone = useRef(false)
  useEffect(() => {
    if (initialSendDone.current) return
    initialSendDone.current = true
    void runResend()
    // 仅挂载时自动发送一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = (fulfilledCode?: string) => {
    const v = fulfilledCode ?? code
    if (v.length === length) {
      onSubmit(v)
    } else {
      setIsInvalid(true)
      setTimeout(() => setIsInvalid(false), 800)
    }
  }

  const resendDisabled = timeLeft > 0 || resending || !onResend

  return (
    <Container>
      <LabelText>请输入6位数验证码</LabelText>
      <RNOTPInput
        length={length}
        value={code}
        onChangeText={setCode}
        isInvalid={isInvalid}
      />

      <VerifyButton onClick={() => handleSubmit(code)} loading={loading} disabled={loading || code.length !== length}>
        <ButtonText>验证</ButtonText>
      </VerifyButton>

      <ResendContainer>
        <Text fontSize={theme.fontSizes.md} color={theme.colors.secondaryText}>
          {timeLeft > 0 ? `${timeLeft}秒后可重新发送` : '可重新发送验证码'}
        </Text>
        <ResendButton
          type="button"
          $disabled={resendDisabled}
          disabled={resendDisabled}
          onClick={() => void runResend()}
        >
          {resendText}
        </ResendButton>
      </ResendContainer>
    </Container>
  )
}

// ================== Styled ==================
const Container = styled.View`
  flex: 1;
  flex-direction: column;
`
const LabelText = styled.Text`
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.md};
  padding: ${({ theme }) => theme.space.sm} 0;
`

const VerifyButton = styled(Button)`
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 44px;
  border-radius: ${({ theme }) => theme.radii.lg};
  margin-top: 10px;
  background-color: ${({ theme }) => theme.colors.primary};
`

const ButtonText = styled.Text`
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 16px;
  font-weight: 600;
`

const ResendContainer = styled(ColumnFlex)`
  width: 100%;
  height: auto;
  margin-top: 22px;
  align-items: center;
  justify-content: center;
`

const ResendButton = styled.button<{ $disabled?: boolean }>`
  margin-top: 14px;
  padding: 8px 12px;
  border: none;
  background: none;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ $disabled, theme }) => ($disabled ? theme.colors.secondaryText : theme.colors.warning)};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
`
